import { useState, useEffect, useCallback } from 'react';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts';
import { useStore } from '../../store';

/**
 * Search history data point
 */
interface HistoryDataPoint {
  date: string;
  searches: number;
}

/**
 * API response for search history
 */
interface SearchHistoryResponse {
  period: number;
  history: HistoryDataPoint[];
  summary: {
    total: number;
    average: number;
    peak: number;
    peakDate: string | null;
  };
}

/**
 * Period option for the selector
 */
interface PeriodOption {
  value: number;
  label: string;
}

const PERIOD_OPTIONS: PeriodOption[] = [
  { value: 7, label: '7 jours' },
  { value: 14, label: '14 jours' },
  { value: 30, label: '30 jours' },
  { value: 60, label: '60 jours' },
  { value: 90, label: '90 jours' },
];

/**
 * Format date for chart display
 */
function formatDateLabel(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
}

/**
 * Format date for tooltip
 */
function formatDateFull(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/**
 * Custom tooltip for the chart
 */
function CustomTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ value: number }>;
  label?: string;
}) {
  if (active && payload && payload.length && label) {
    return (
      <div className="bg-white px-4 py-3 rounded-xl shadow-lg border border-slate-200">
        <p className="text-sm font-medium text-slate-900">{formatDateFull(label)}</p>
        <p className="text-2xl font-bold text-primary-600 mt-1">
          {payload[0]?.value.toLocaleString('fr-FR')}
        </p>
        <p className="text-xs text-slate-500">recherches</p>
      </div>
    );
  }
  return null;
}

/**
 * StatsPage Component
 *
 * Admin page for viewing search statistics with interactive charts.
 * Features:
 * - Line chart showing search trends over time
 * - Period selector (7, 14, 30, 60, 90 days)
 * - Summary statistics (total, average, peak)
 */
export function StatsPage() {
  const token = useStore((state) => state.token);
  const [period, setPeriod] = useState<number>(7);
  const [data, setData] = useState<SearchHistoryResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch search history from API
   */
  const fetchHistory = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`/api/admin/stats/history?days=${period}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error?.message || 'Erreur lors du chargement des statistiques');
      }

      setData(result.data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur inconnue';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [token, period]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  // Prepare chart data with formatted labels
  const chartData = data?.history.map((item) => ({
    ...item,
    dateLabel: formatDateLabel(item.date),
  })) || [];

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Statistiques</h1>
          <p className="text-slate-500 text-sm mt-1">Historique des recherches</p>
        </div>

        {/* Period selector */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500">Période :</span>
          <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
            {PERIOD_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setPeriod(option.value)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  period === option.value
                    ? 'bg-white text-primary-600 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-100">
          <p className="text-sm text-slate-500 mb-1">Total recherches</p>
          {isLoading ? (
            <div className="h-8 bg-slate-200 rounded animate-pulse"></div>
          ) : (
            <p className="text-2xl font-bold text-slate-900">
              {data?.summary.total.toLocaleString('fr-FR') ?? 0}
            </p>
          )}
          <p className="text-xs text-slate-400 mt-1">sur {period} jours</p>
        </div>

        <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-100">
          <p className="text-sm text-slate-500 mb-1">Moyenne / jour</p>
          {isLoading ? (
            <div className="h-8 bg-slate-200 rounded animate-pulse"></div>
          ) : (
            <p className="text-2xl font-bold text-slate-900">
              {data?.summary.average.toLocaleString('fr-FR') ?? 0}
            </p>
          )}
          <p className="text-xs text-slate-400 mt-1">recherches</p>
        </div>

        <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-100">
          <p className="text-sm text-slate-500 mb-1">Pic</p>
          {isLoading ? (
            <div className="h-8 bg-slate-200 rounded animate-pulse"></div>
          ) : (
            <p className="text-2xl font-bold text-primary-600">
              {data?.summary.peak.toLocaleString('fr-FR') ?? 0}
            </p>
          )}
          <p className="text-xs text-slate-400 mt-1">
            {data?.summary.peakDate ? formatDateLabel(data.summary.peakDate) : '-'}
          </p>
        </div>

        <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-100">
          <p className="text-sm text-slate-500 mb-1">Jours actifs</p>
          {isLoading ? (
            <div className="h-8 bg-slate-200 rounded animate-pulse"></div>
          ) : (
            <p className="text-2xl font-bold text-slate-900">
              {data?.history.filter((d) => d.searches > 0).length ?? 0}
            </p>
          )}
          <p className="text-xs text-slate-400 mt-1">sur {period} jours</p>
        </div>
      </div>

      {/* Chart */}
      <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
        <h2 className="font-semibold text-lg text-slate-900 mb-4 flex items-center gap-2">
          <span>📈</span>
          Évolution des recherches
        </h2>

        {isLoading ? (
          <div className="h-80 flex items-center justify-center">
            <div className="flex items-center gap-3">
              <svg className="w-6 h-6 animate-spin text-primary-500" fill="none" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span className="text-slate-500">Chargement des données...</span>
            </div>
          </div>
        ) : chartData.length === 0 ? (
          <div className="h-80 flex items-center justify-center text-slate-500">
            <p>Aucune donnée disponible pour cette période</p>
          </div>
        ) : (
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorSearches" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="dateLabel"
                  stroke="#94a3b8"
                  fontSize={12}
                  tickLine={false}
                  axisLine={{ stroke: '#e2e8f0' }}
                  interval={Math.floor(chartData.length / 7)}
                />
                <YAxis
                  stroke="#94a3b8"
                  fontSize={12}
                  tickLine={false}
                  axisLine={{ stroke: '#e2e8f0' }}
                  tickFormatter={(value) => value.toLocaleString('fr-FR')}
                />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="searches"
                  stroke="#6366f1"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorSearches)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Additional info */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div>
            <p className="text-sm text-blue-800 font-medium">À propos des statistiques</p>
            <p className="text-sm text-blue-700 mt-1">
              Les données sont agrégées depuis les recherches effectuées par tous les utilisateurs
              (gratuits, premium et administrateurs). L'historique est conservé indéfiniment.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StatsPage;
